import React from "react";
import BusinessIcon from "@mui/icons-material/Business";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DiamondIcon from "@mui/icons-material/Diamond";

export type TierId = "starter" | "core" | "premium" | "measure" | "enable";

export interface TierDisplay {
	id: TierId;
	name: string;
	description: string;
	icon: React.ReactNode;
	included: string[];
	excluded: string[];
	buttonText: string;
	featured: boolean;
}

export const TIER_DISPLAY: Record<TierId, TierDisplay> = {
	starter: {
		id: "starter",
		name: "Starter",
		description:
			"For micro and small businesses tracking emissions across the business",
		icon: React.createElement(BusinessIcon),
		included: [
			"Basic carbon footprint tracking",
			"Up to 3 facilities",
			"Email support",
			"Basic dashboard (top charts)",
		],
		excluded: [
			"Emissions reports",
			"Emissions simulations",
			"Compliance reporting (AASB S2)",
			"Goal setting & tracking",
			"Benchmarking tools",
			"Custom reporting",
			"API access",
			"Online support (48h SLA)",
			"Priority email & phone support",
			"Dedicated account manager",
			"Custom integrations",
			"Custom training sessions",
			"SLA guarantees",
			"White-label option",
		],
		buttonText: "Start Free",
		featured: false,
	},
	core: {
		id: "core",
		name: "Core",
		description:
			"For suppliers to AASB S2 / Safeguard reporters and businesses that need full emissions insight",
		icon: React.createElement(TrendingUpIcon),
		included: [
			"Everything in Starter",
			"Unlimited facilities",
			"Automated reporting",
			"Emissions simulations",
			"Full dashboard (all charts)",
			"Compliance reporting (AASB S2 aligned)",
			"Goal setting & tracking",
			"Benchmarking tools",
			"Custom reporting",
			"Online support (48h SLA)",
		],
		excluded: [
			"Dedicated account manager",
			"Custom integrations",
			"API access (auto-enabled)",
			"Custom training sessions",
			"SLA guarantees",
			"Priority phone support",
			"White-label option",
		],
		buttonText: "Get Core",
		featured: true,
	},
	premium: {
		id: "premium",
		name: "Premium",
		description:
			"Core, plus a dedicated account manager and the human-services tier for compliance and integrations",
		icon: React.createElement(DiamondIcon),
		included: [
			"Everything in Core",
			"Dedicated account manager",
			"Custom integrations (on demand)",
			"API access (on demand)",
			"Custom training sessions",
			"SLA guarantees",
			"Priority email & phone support",
			"White-label option",
		],
		excluded: [],
		buttonText: "Get Premium",
		featured: false,
	},

	measure: {
		id: "measure",
		name: "Premium",
		description:
			"For small businesses that need to answer a customer's emissions data request",
		icon: React.createElement(BusinessIcon),
		included: [
			"Scope 1 & 2 emissions tracking",
			"Spend-based Scope 3 estimates",
			"One-click customer data pack",
			"AI bill extraction & spreadsheet import",
			"Full dashboard (all charts)",
			"Single entity, 2 users",
			"Email support",
		],
		excluded: [
			"Detailed Scope 3 (travel, freight, commuting)",
			"15-category Scope 3 breakdown",
			"AASB S2 disclosure & board summary",
			"Assurance workspace (methodology, evidence, audit trail)",
			"Baselines & restatement",
			"Market-based Scope 2 & custom factors",
			"Abatement investment modelling",
			"Emissions reports",
			"Supplier data requests",
			"Group consolidation",
		],
		buttonText: "Get Premium",
		featured: false,
	},
	enable: {
		id: "enable",
		name: "Executive",
		description:
			"The full compliance platform for AASB S2 reporters and their advisors",
		icon: React.createElement(DiamondIcon),
		included: [
			"Everything in Premium",
			"Detailed Scope 3 (travel, freight, commuting)",
			"Full 15-category Scope 3 with coverage view",
			"Supplier-specific & custom emission factors",
			"Market-based Scope 2",
			"Baselines & restatement (assurance-grade)",
			"AASB S2 disclosure + board summary",
			"Assurance workspace: methodology, evidence & audit trail",
			"Supplier data requests",
			"Group consolidation",
			"Abatement investment modelling & emissions reports",
			"Unlimited facilities & users",
			"Priority support",
		],
		excluded: [],
		buttonText: "Get Executive",
		featured: true,
	},
};

/**
 * Which tier generation the public pricing page sells. Flipping this
 * array IS the launch switch for the two-tier structure (tracker item 8).
 */
export const PRICING_PAGE_TIERS: TierId[] = ["starter", "measure", "enable"];

export interface CatalogPlan {
	tier: TierId;
	stripePriceId: string | null;
	amountCents: number;
	currency: string;
}

/**
 * Coerce an unknown plan string (e.g. from a query param) to a valid TierId.
 * Defaults to "starter" for unknown / missing values, which matches the
 * backend's normalizeTier behavior in tierFeatures.ts.
 */
export function coerceTierId(value: string | null | undefined): TierId {
	if (
		value === "core" ||
		value === "premium" ||
		value === "measure" ||
		value === "enable"
	)
		return value;
	return "starter";
}

/**
 * Format an amountCents value for the price line ("Free" or "AUD 1,000 / year").
 */
export function formatTierPrice(plan: CatalogPlan | undefined): string {
	if (!plan || plan.amountCents === 0) return "Free";
	return `${plan.currency} ${(plan.amountCents / 100).toLocaleString()}`;
}
